import React, { useEffect, useMemo, useState } from 'react'
import {
  CBadge,
  CCard,
  CCardBody,
  CCol,
  CFormInput,
  CFormSelect,
  CFormSwitch,
  CRow,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilSearch } from '@coreui/icons'
import PlatformAnalyticsService from 'src/service/PlatformAnalyticsService'
import { ErrorState, formatNumber } from '../AnalyticsV2/common'
import { DeviceCell, OnlineDot, errorMessage, formatDateTime, timeAgo } from './platformHelpers'

const columns = [
  { key: 'full_name', label: 'Person', _style: { minWidth: 220 } },
  { key: 'games', label: 'Games', _style: { minWidth: 200 } },
  { key: 'device', label: 'Device', sorter: false, _style: { minWidth: 170 } },
  { key: 'current_screen', label: 'Last screen', sorter: false },
  { key: 'sessions', label: 'Sessions' },
  { key: 'screen_views', label: 'Screens viewed' },
  { key: 'last_seen', label: 'Last seen' },
].map((c) => ({ filter: false, ...c }))

const UsersPanel = ({ filters, gameOptions, gameId, onGameChange, onOpenUser, reloadToken }) => {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(25)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [online, setOnline] = useState(false)
  const [sorter, setSorter] = useState({ column: 'last_seen', state: 'desc' })
  const [state, setState] = useState({ data: null, loading: true, error: null })

  // Debounce typing so every keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 350)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => setPage(1), [gameId, online, filters])

  useEffect(() => {
    let cancelled = false
    setState((s) => ({ ...s, loading: true, error: null }))
    PlatformAnalyticsService.getUsers(filters, {
      page,
      limit,
      game_id: gameId || undefined,
      search,
      online: online ? '1' : undefined,
      sort: sorter?.column,
      order: sorter?.state,
    })
      .then((res) => {
        if (cancelled) return
        setState(
          res?.success
            ? { data: res.data, loading: false, error: null }
            : { data: null, loading: false, error: errorMessage(res) },
        )
      })
      .catch(
        (err) => !cancelled && setState({ data: null, loading: false, error: errorMessage(err) }),
      )
    return () => {
      cancelled = true
    }
  }, [filters, page, limit, gameId, search, online, sorter, reloadToken])

  const sortedGames = useMemo(
    () =>
      [...(gameOptions || [])].sort((a, b) => (a.game_name || '').localeCompare(b.game_name || '')),
    [gameOptions],
  )

  const total = state.data?.total ?? 0

  return (
    <CCard>
      <CCardBody>
        <CRow className="g-2 mb-3 align-items-center">
          <CCol xs={12} md={4}>
            <div className="position-relative">
              <CIcon icon={cilSearch} className="pa-search-icon text-medium-emphasis" />
              <CFormInput
                size="sm"
                className="ps-5"
                placeholder="Search name, email or user ID"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search people"
              />
            </div>
          </CCol>
          <CCol xs={12} md={4}>
            <CFormSelect
              size="sm"
              value={gameId || ''}
              onChange={(e) => onGameChange(e.target.value ? Number(e.target.value) : null)}
              aria-label="Filter by game"
            >
              <option value="">All games</option>
              {sortedGames.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.game_name} (#{g.id})
                </option>
              ))}
            </CFormSelect>
          </CCol>
          <CCol xs={7} md={2}>
            <CFormSwitch
              id="pa-online-only"
              label="Online now only"
              checked={online}
              onChange={(e) => setOnline(e.target.checked)}
            />
          </CCol>
          <CCol xs={5} md={2} className="text-end small text-medium-emphasis">
            {state.loading ? '…' : `${formatNumber(total)} ${total === 1 ? 'person' : 'people'}`}
          </CCol>
        </CRow>

        {state.error ? (
          <ErrorState message={state.error} onRetry={() => setSorter({ ...sorter })} />
        ) : (
          <div className="ge-datatable ge-datatable--no-select ge-datatable--static pa-games-table">
            <CSmartTable
              items={state.data?.rows || []}
              columns={columns}
              loading={state.loading}
              columnSorter={{ external: true }}
              sorterValue={sorter}
              onSorterChange={(s) => {
                setPage(1)
                setSorter(s?.state ? s : { column: 'last_seen', state: 'desc' })
              }}
              pagination={{ external: true }}
              paginationProps={{ activePage: page, pages: Math.max(1, Math.ceil(total / limit)) }}
              onActivePageChange={setPage}
              itemsPerPage={limit}
              itemsPerPageSelect
              onItemsPerPageChange={(n) => {
                setPage(1)
                setLimit(n)
              }}
              noItemsLabel={
                online ? 'Nobody matching is online right now' : 'No app users match these filters'
              }
              clickableRows
              onRowClick={(item) => onOpenUser({ id: item.user_id, full_name: item.full_name })}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
              scopedColumns={{
                full_name: (item) => (
                  <td>
                    <div className="d-flex align-items-center gap-2 fw-semibold">
                      <OnlineDot online={item.online} />
                      <span className="text-truncate pa-game-name">
                        {item.full_name || `User #${item.user_id}`}
                      </span>
                    </div>
                    <div className="small text-medium-emphasis text-truncate pa-game-name">
                      #{item.user_id} · {item.email || 'no email'}
                    </div>
                  </td>
                ),
                games: (item) => {
                  const list = item.game_list || []
                  return (
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {list.slice(0, 2).map((g) => (
                          <CBadge
                            key={g.game_id}
                            color="secondary"
                            shape="rounded-pill"
                            className="pa-game-badge text-truncate"
                            title={`${g.game_name} — ${g.sessions} sessions`}
                          >
                            {g.game_name}
                          </CBadge>
                        ))}
                        {list.length > 2 && (
                          <CBadge
                            color="light"
                            textColor="dark"
                            shape="rounded-pill"
                            title={list
                              .slice(2)
                              .map((g) => g.game_name)
                              .join(', ')}
                          >
                            +{list.length - 2}
                          </CBadge>
                        )}
                      </div>
                    </td>
                  )
                },
                device: (item) => (
                  <td>
                    <DeviceCell row={item} />
                  </td>
                ),
                current_screen: (item) => (
                  <td>
                    <span className="av2-screen-name small">{item.current_screen || '—'}</span>
                  </td>
                ),
                sessions: (item) => <td>{formatNumber(item.sessions)}</td>,
                screen_views: (item) => <td>{formatNumber(item.screen_views)}</td>,
                last_seen: (item) => (
                  <td className="text-nowrap" title={formatDateTime(item.last_seen)}>
                    {item.online ? (
                      <span className="text-success fw-semibold">Online now</span>
                    ) : (
                      timeAgo(item.last_seen) || '—'
                    )}
                  </td>
                ),
              }}
            />
          </div>
        )}
      </CCardBody>
    </CCard>
  )
}

export default UsersPanel
